'use client';

import Image from 'next/image';
import { MapPin } from 'lucide-react';
import { Memorial } from '@/lib/types';
import { formatDate, calculateAge, formatLifeSpan } from '@/lib/utils';

interface MemorialHeaderProps {
  memorial: Memorial;
}

export function MemorialHeader({ memorial }: MemorialHeaderProps) {
  const fullName = `${memorial.firstName || ''} ${memorial.lastName || ''}`.trim();
  const age = calculateAge(memorial.dateOfBirth, memorial.dateOfDeath);
  const lifeSpan = formatLifeSpan(memorial.dateOfBirth, memorial.dateOfDeath);

  return (
    <header className="relative bg-gradient-to-b from-memorial-primary to-memorial-primary/90 text-white py-12 md:py-16">
      {/* Decorative overlay */}
      <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-5" />

      <div className="relative max-w-4xl mx-auto px-4 text-center">
        {/* Portrait */}
        <div className="mb-6">
          <div className="relative inline-block">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white/30 shadow-xl mx-auto">
              {memorial.imagePath ? (
                <Image
                  src={memorial.imagePath}
                  alt={fullName}
                  width={192}
                  height={192}
                  className="w-full h-full object-cover"
                  priority
                />
              ) : (
                <div className="w-full h-full bg-memorial-secondary flex items-center justify-center text-4xl font-serif">
                  {memorial.firstName?.charAt(0) || ''}{memorial.lastName?.charAt(0) || ''}
                </div>
              )}
            </div>
            {/* Decorative ring */}
            <div className="absolute inset-0 rounded-full border-2 border-white/10 scale-110" />
          </div>
        </div>

        {/* Name */}
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal mb-3">
          {fullName}
        </h1>

        {/* Life dates */}
        <div className="text-xl md:text-2xl text-white/90 mb-2">
          {formatDate(memorial.dateOfBirth)} – {formatDate(memorial.dateOfDeath)}
        </div>

        {/* Age */}
        {age !== null && (
          <div className="text-lg text-white/70 mb-4">
            ({age} Jahre)
          </div>
        )}

        {/* Location */}
        {memorial.placeOfResidence && (
          <div className="flex items-center justify-center gap-2 text-white/80">
            <MapPin className="w-4 h-4" />
            <span>{memorial.placeOfResidence}</span>
          </div>
        )}

        {/* Decorative divider */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="w-12 h-px bg-white/30" />
          <div className="w-2 h-2 rounded-full bg-memorial-accent" />
          <div className="w-12 h-px bg-white/30" />
        </div>
      </div>
    </header>
  );
}
